import {
  Button,
  Divider,
  Empty,
  Modal,
  Space,
  Typography,
  Upload,
  message,
} from "antd";
import React from "react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { _ObjectType, FilesReturnType, S3FileWithUrl } from "~/types";
import { getFileUrl, INTENTS } from "~/utils";
import { useTranslation } from "react-i18next";
import { useFetcher, useNavigation } from "react-router-dom";
import { Loading3QuartersOutlined, PlusOutlined } from "@ant-design/icons";
import * as APITypes from "~/API";
import { S3ServiceLambda } from "~/services/s3";

export const ImageModal = ({
  files,
  entityID,
  entityType,
}: {
  files: FilesReturnType;
  entityID: string;
  entityType: _ObjectType;
}) => {
  const { t } = useTranslation();
  const [showImageModal, setShowImageModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [allowUpload, setAllowUpload] = React.useState(false);

  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [currentImage, setCurrentImage] = React.useState<S3FileWithUrl | null>(
    null,
  );
  const [newImage, setNewImage] = React.useState<File | null>(null);
  const fetcher = useFetcher();
  const toggleImageModal = () => setShowImageModal(!showImageModal);

  const navigation = useNavigation();

  const isSubmitting =
    navigation.state === "submitting" || fetcher.state !== "idle";
  const isLoading = loading || isSubmitting;

  const shouldDisable = isLoading;

  const beforeUpload = (file: any) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("Image must smaller than 2MB!");
    }

    setAllowUpload(isJpgOrPng && isLt2M);
    return isJpgOrPng && isLt2M;
  };

  const handleFileChange = (info: any) => {
    if (info.fileList.length > 0) {
      setLoading(true);

      // Use the first file in the list
      const file: File = info.fileList[0].originFileObj;
      setNewImage(file);
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    const payload = {
      entityID,
      entityType,
      name: newImage?.name,
      type: APITypes.EntityFieldType.Image,
    };

    if (!newImage) {
      message.error("No file selected. Please select a file to upload");
      return;
    }

    const result = await S3ServiceLambda.upload({
      entityType,
      entityID,
      file: newImage,
      name: payload.name || "",
    });

    if (result.success) {
      message.success("File successfully uploaded");
    } else {
      message.error("File upload failed. Please try again");
    }

    setCurrentImage(null);
    setUploadingImage(false);
    setNewImage(null);

    fetcher.submit(
      { body: "Empty value to trigger action" },
      { method: "post" },
    );
  };

  const handleDeleteFile = () => {
    fetcher.submit(
      { body: currentImage?.Key || "", intent: INTENTS._delete_file },
      { method: "post" },
    );
    setCurrentImage(null);
  };

  return (
    <>
      <Space direction="vertical" key={"image-modal-space"}>
        <div>
          {files.images.length > 0 ? (
            <PhotoProvider>
              <div>
                {files.images.map((file, index) => {
                  const url = getFileUrl(file);

                  return (
                    <PhotoView key={index} src={url}>
                      <img src={url} alt="" width={200} height={200} />
                    </PhotoView>
                  );
                })}
              </div>
            </PhotoProvider>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </div>
        <Button
          onClick={() => toggleImageModal()}
          size="small"
          disabled={shouldDisable}
        >
          {t("View_images")}
        </Button>
      </Space>

      <Modal
        open={showImageModal}
        footer={[]}
        onCancel={toggleImageModal}
        style={{ width: "fit-content", minWidth: 400 }}
      >
        <div style={{ display: "flex" }}>
          <Space direction="vertical">
            <Typography.Text strong>
              {t("Images")} {isLoading && <Loading3QuartersOutlined spin />}
            </Typography.Text>
            {(files?.images || [])?.map((file) => {
              return (
                <Button
                  onClick={() => {
                    setUploadingImage(false);
                    setCurrentImage(file);
                  }}
                  size="small"
                  type="text"
                  key={file.Key || ""}
                  disabled={isLoading}
                >
                  {(file.Key || "").split("/").pop() || ""}
                  {currentImage?.Key === file.Key && " >"}
                </Button>
              );
            })}
            <Button
              onClick={() => {
                setUploadingImage(true);
                setCurrentImage(null);
              }}
              size="small"
              type="text"
              disabled={isLoading}
            >
              {t("Upload_Image")}
              {uploadingImage && " >"}
            </Button>
          </Space>

          <Divider type="vertical" />

          {uploadingImage ? (
            <Space direction="vertical">
              <Typography.Text strong>{t("Details")}</Typography.Text>
              <Upload
                listType="picture-card"
                maxCount={1}
                beforeUpload={beforeUpload}
                onPreview={() => {}}
                onChange={handleFileChange}
              >
                <button style={{ border: 0, background: "none" }} type="button">
                  {loading ? (
                    <Loading3QuartersOutlined spin />
                  ) : (
                    <PlusOutlined />
                  )}
                  <div style={{ marginTop: 8 }}>{t("Upload_Image")}</div>
                </button>
              </Upload>
              <Button
                onClick={() => handleFileUpload()}
                type="primary"
                size="small"
                disabled={!newImage || !allowUpload}
              >
                {t("Submit")}
              </Button>
            </Space>
          ) : (
            <Space direction="vertical">
              <Typography.Text strong>{t("Details")}</Typography.Text>
              <div style={{ width: "100%", marginTop: "2rem" }}>
                {currentImage ? (
                  <img
                    src={getFileUrl(currentImage)}
                    alt=""
                    width={200}
                    height={200}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </div>
              <Button
                onClick={() => handleDeleteFile()}
                type="primary"
                danger
                size="small"
                disabled={!currentImage}
              >
                {t("Delete")}
              </Button>
            </Space>
          )}
        </div>
      </Modal>
    </>
  );
};
